import { useCallback, useEffect, useMemo } from 'react';
import { PageStatus, type PageType } from '@/types/store';
import { useFunnelEditorStore } from '../stores/funnelEditorStore';
import { arePreviousStepsComplete } from '../logic';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import type { FunnelPageItem } from '../types';

const FUNNEL_PAGE_TYPE = 'funnel-page' as PageType;

interface UseFunnelEditorSelectionArgs {
    funnelPages: FunnelPageItem[];
    savedFunnelPages: FunnelPageItem[];
    maxSavedOrder: number;
}

/**
 * Reads selection + draft state from the editor store and derives the
 * currently-selected page (including the virtual "suggested slot" draft),
 * its step number, role flags, and unlock flags. Also exposes the
 * `handleSelect*` / `handleAddPage` UI callbacks.
 */
export function useFunnelEditorSelection({
    funnelPages,
    savedFunnelPages,
    maxSavedOrder,
}: UseFunnelEditorSelectionArgs) {
    const selectedPageId = useFunnelEditorStore(s => s.selectedPageId);
    const selectedSuggestedIndex = useFunnelEditorStore(s => s.selectedSuggestedIndex);
    const pendingSelectOrder = useFunnelEditorStore(s => s.pendingSelectOrder);
    const hasDraftPage = useFunnelEditorStore(s => s.hasDraftPage);
    const setSelectedPageId = useFunnelEditorStore(s => s.setSelectedPageId);
    const setPendingSelectOrder = useFunnelEditorStore(s => s.setPendingSelectOrder);
    const setHasDraftPage = useFunnelEditorStore(s => s.setHasDraftPage);
    const selectPage = useFunnelEditorStore(s => s.selectPage);
    const selectSuggested = useFunnelEditorStore(s => s.selectSuggested);

    useEffect(() => {
        if (pendingSelectOrder === null) return;
        const match = savedFunnelPages.find(p => p.order === pendingSelectOrder);
        if (match) {
            selectPage(match.page_id);
            setPendingSelectOrder(null);
        }
    }, [savedFunnelPages, pendingSelectOrder, selectPage, setPendingSelectOrder]);

    useEffect(() => {
        // A suggested row is active → don't auto-select a real page.
        if (selectedSuggestedIndex !== null) return;
        if (pendingSelectOrder !== null) return;
        if (funnelPages.length === 0) {
            setSelectedPageId(null);
            return;
        }
        const hasSelection = selectedPageId !== null && funnelPages.some(p => p.page_id === selectedPageId);
        if (!hasSelection) {
            const firstPage = funnelPages[0];
            if (firstPage) setSelectedPageId(firstPage.page_id);
        }
    }, [funnelPages, selectedPageId, selectedSuggestedIndex, pendingSelectOrder, setSelectedPageId]);

    // When a suggested slot is active, we synthesize a virtual draft page so
    // the editor can render its normal UI with role-driven copy for that slot.
    const virtualSuggestedPage = useMemo<FunnelPageItem | null>(() => {
        if (selectedSuggestedIndex === null) return null;
        const maxRawSavedOrder = savedFunnelPages.reduce((max, p) => Math.max(max, p.rawOrder ?? p.order), 0);
        return {
            page_id: DRAFT_FUNNEL_PAGE_ID,
            title: `Page ${selectedSuggestedIndex + 1}`,
            type: FUNNEL_PAGE_TYPE,
            status: PageStatus.DRAFT,
            order: selectedSuggestedIndex + 1,
            rawOrder: maxRawSavedOrder + 1,
        };
    }, [savedFunnelPages, selectedSuggestedIndex]);

    const selectedPage = useMemo(
        () => virtualSuggestedPage ?? funnelPages.find(p => p.page_id === selectedPageId) ?? null,
        [virtualSuggestedPage, funnelPages, selectedPageId]
    );

    // Step number reflects the slot the user is editing — based on `page.order`
    // so pages saved to higher slots (e.g. order=3 while slots 1/2 are empty)
    // keep their role instead of collapsing to array index.
    const selectedStepNumber = (() => {
        if (selectedSuggestedIndex !== null) return selectedSuggestedIndex + 1;
        if (!selectedPage) return 0;
        return selectedPage.order;
    })();

    const isDraftSelected = !!virtualSuggestedPage || selectedPageId === DRAFT_FUNNEL_PAGE_ID;
    const isLegacySelected =
        !!selectedPage && !virtualSuggestedPage && !isDraftSelected && selectedPage.type !== FUNNEL_PAGE_TYPE;

    const isSelectedStepUnlocked = useMemo(
        () => arePreviousStepsComplete(savedFunnelPages, selectedStepNumber),
        [savedFunnelPages, selectedStepNumber]
    );

    const isAddPageUnlocked = useMemo(
        () => arePreviousStepsComplete(savedFunnelPages, maxSavedOrder + 1),
        [savedFunnelPages, maxSavedOrder]
    );

    const handleSelectPage = useCallback((pageId: number) => selectPage(pageId), [selectPage]);
    const handleSelectSuggested = useCallback((index: number) => selectSuggested(index), [selectSuggested]);

    const handleAddPage = useCallback(() => {
        if (hasDraftPage) {
            selectPage(DRAFT_FUNNEL_PAGE_ID);
            return;
        }
        setHasDraftPage(true);
        selectPage(DRAFT_FUNNEL_PAGE_ID);
    }, [hasDraftPage, selectPage, setHasDraftPage]);

    return {
        selectedPageId,
        selectedPage,
        selectedStepNumber,
        selectedSuggestedIndex,
        isDraftSelected,
        isLegacySelected,
        hasDraftPage,
        isSelectedStepUnlocked,
        isAddPageUnlocked,
        handleSelectPage,
        handleSelectSuggested,
        handleAddPage,
    };
}
